import { SearchAccountImage } from '@approvalmax/ui';
import { Box, Divider, Flex, NoContent, Text } from '@approvalmax/ui/src/components';
import { useAtomValue } from 'jotai';
import { GlobalLoadingBar } from 'modules/page';
import { Fragment, memo } from 'react';
import { useNetSuiteMatchingSuggestions } from 'shared/hooks/(matchings)';

import { netSuiteSourceMatchingLineState } from '../../NetSuiteMatchingPopup.states';
import { MatchingLineItems } from '../MatchingLineItems/MatchingLineItems';
import { messages } from './PurchaseOrdersList.messages';
import type { PurchaseOrdersListProps } from './PurchaseOrdersList.types';

export const PurchaseOrdersList = memo((props: PurchaseOrdersListProps) => {
    const { request, currencyISOCode, isRequestForm } = props;

    const sourceLine = useAtomValue(netSuiteSourceMatchingLineState);

    const { suggestions, isFetching: isFetchingNetSuiteMatchingSuggestions } = useNetSuiteMatchingSuggestions({
        request,
        lineType: sourceLine?.lineType,
        lineNumber: !isRequestForm ? sourceLine?.lineNumber : undefined,
        accountId: isRequestForm && sourceLine && 'account' in sourceLine ? sourceLine?.account?.id : undefined,
        categoryId: isRequestForm && sourceLine && 'category' in sourceLine ? sourceLine?.category?.id : undefined,
        itemId: isRequestForm && sourceLine && 'item' in sourceLine ? sourceLine?.item?.id : undefined,
    });

    if (isFetchingNetSuiteMatchingSuggestions) {
        return <GlobalLoadingBar isLoading={isFetchingNetSuiteMatchingSuggestions} />;
    }

    if (!suggestions?.matchingLinesInfos.length) {
        return (
            <Flex alignItems='center' justifyContent='center' grow={1}>
                <NoContent icon={<SearchAccountImage />} heading={messages.emptyMessage} width='100%' />
            </Flex>
        );
    }

    return (
        <Box width='100%'>
            <Text font='headline' fontSize='xxsmall' fontWeight='medium' color='midnight80' spacing='12 24'>
                {messages.title}
            </Text>

            {suggestions.matchingLinesInfos.map((info, index) => (
                <Fragment key={`${info.requestId}-${info.line?.lineNumber}`}>
                    {index !== 0 && <Divider color='midnight50' />}

                    <MatchingLineItems
                        request={request}
                        lineType={sourceLine?.lineType}
                        matchingInfo={info}
                        currencyISOCode={currencyISOCode}
                    />
                </Fragment>
            ))}
        </Box>
    );
});

PurchaseOrdersList.displayName = 'PurchaseOrdersList';
